import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabaseAuth } from '../services/supabaseAuth';
import { SupabaseAuthProvider, SupabaseSession, SupabaseUser } from '../types/supabase';

interface AuthContextValue {
  isConfigured: boolean;
  loading: boolean;
  session: SupabaseSession | null;
  user: SupabaseUser | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  startOAuth: (provider: Extract<SupabaseAuthProvider, 'google' | 'facebook'>) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      if (!supabaseAuth.isConfigured) {
        setLoading(false);
        return;
      }

      try {
        const fromHash = await supabaseAuth.initializeFromHash();
        if (fromHash && isMounted) {
          setSession(fromHash);
          setUser(fromHash.user);
        }

        const existing = fromHash ?? supabaseAuth.loadPersistedSession();
        if (!existing) {
          setLoading(false);
          return;
        }

        let resolved: SupabaseSession | null = existing;
        const now = Math.floor(Date.now() / 1000);
        if (existing.expiresAt - now < 60) {
          resolved = await supabaseAuth.refresh(existing) ?? null;
        } else if (!existing.user) {
          await supabaseAuth.getUser(existing);
        }

        if (resolved && isMounted) {
          const ensuredUser = resolved.user ?? (await supabaseAuth.getUser(resolved));
          setSession(resolved);
          setUser(ensuredUser ?? null);
        }
      } catch (err) {
        console.error('[AuthContext] Failed to initialize session', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unable to initialize auth');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  const contextValue = useMemo<AuthContextValue>(() => ({
    isConfigured: supabaseAuth.isConfigured,
    loading,
    session,
    user,
    error,
    signIn: async (email: string, password: string) => {
      setError(null);
      try {
        const newSession = await supabaseAuth.signIn(email, password);
        setSession(newSession);
        const ensuredUser = newSession.user ?? (await supabaseAuth.getUser(newSession));
        setUser(ensuredUser ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to sign in');
        throw err;
      }
    },
    signUp: async (email: string, password: string) => {
      setError(null);
      try {
        const newSession = await supabaseAuth.signUp(email, password);
        setSession(newSession);
        const ensuredUser = newSession.user ?? (await supabaseAuth.getUser(newSession));
        setUser(ensuredUser ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to sign up');
        throw err;
      }
    },
    sendMagicLink: async (email: string) => {
      setError(null);
      await supabaseAuth.sendMagicLink(email);
    },
    startOAuth: (provider: Extract<SupabaseAuthProvider, 'google' | 'facebook'>) => {
      setError(null);
      supabaseAuth.startOAuth(provider);
    },
    signOut: async () => {
      setError(null);
      await supabaseAuth.signOut(session);
      setSession(null);
      setUser(null);
    },
    refresh: async () => {
      if (!session) {
        return;
      }
      try {
        const refreshed = await supabaseAuth.refresh(session);
        if (refreshed) {
          setSession(refreshed);
          const ensuredUser = refreshed.user ?? (await supabaseAuth.getUser(refreshed));
          setUser(ensuredUser ?? null);
        }
      } catch (refreshError) {
        console.error('[AuthContext] Failed to refresh session', refreshError);
        setSession(null);
        setUser(null);
        setError(refreshError instanceof Error ? refreshError.message : 'Session expired');
      }
    },
  }), [error, loading, session, user]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

